/**
 * document-type-fields — klient API „pól typu dokumentu” oraz czysta logika po stronie
 * przeglądarki: klucze zapytań, wywołania tras `/document-types/:id/fields`, mapa stanów pól
 * typu dla karty dokumentu i sprawdzenie wymaganych pól dodatkowych przed wysyłką.
 *
 * Reguła (po stronie serwera: `apps/api/lib/documents/type-fields.ts`): typ z ustawieniem ITE →
 * pola z ustawienia, reszta „nie dotyczy”; typ bez ustawienia → wszystkie pola klasy; nakładka
 * firmy nadpisuje stan pola; pole daty numeracji jest zawsze wymagane.
 */

import type {
  DocumentClass,
  DocumentTypeFieldsResponse,
  DocumentTypeFieldsSummaryItem,
  DocumentTypeFieldsSummaryResponse,
  DocumentTypeFieldState,
  EffectiveDocumentTypeField,
} from '@saas/shared';

import type { EngineFormField } from '@/components/kartoteki/engine/engine-types';
import { api } from '@/lib/api';

// ── Klucze zapytań ──────────────────────────────────────────────────────────────

/** Prefiks kluczy efektywnych pól typu — unieważnienie po zmianie ustawienia pól. */
export const DOCUMENT_TYPE_FIELDS_QUERY_PREFIX = 'document-type-fields' as const;

export function documentTypeFieldsKey(typeId: string, companyId: string | null) {
  return [DOCUMENT_TYPE_FIELDS_QUERY_PREFIX, typeId, companyId] as const;
}

/**
 * Podsumowanie pól typów klasy. Zaczyna się od `['document-types', klasa]`, więc unieważnienie
 * listy typów klasy odświeża je razem z listą.
 */
export function documentTypeFieldsSummaryKey(docClass: DocumentClass, companyId: string | null) {
  return ['document-types', docClass, 'fields-summary', companyId] as const;
}

// ── Wywołania API ───────────────────────────────────────────────────────────────

function companyQuery(companyId: string | null): string {
  return companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
}

export async function fetchDocumentTypeFields(
  typeId: string,
  companyId: string | null,
): Promise<DocumentTypeFieldsResponse> {
  const res = await api.get<DocumentTypeFieldsResponse>(
    `/document-types/${typeId}/fields${companyQuery(companyId)}`,
  );
  return res.data;
}

/** Ustawia stan pola w nakładce firmy; zwraca przeliczone pola typu. */
export async function putDocumentTypeFieldState(
  typeId: string,
  fieldSlug: string,
  companyId: string | null,
  state: DocumentTypeFieldState,
): Promise<DocumentTypeFieldsResponse> {
  const res = await api.put<DocumentTypeFieldsResponse>(
    `/document-types/${typeId}/fields/${encodeURIComponent(fieldSlug)}`,
    { companyId, state },
  );
  return res.data;
}

/** „Przywróć ustawienie ITE” dla jednego pola. */
export async function deleteDocumentTypeFieldOverride(
  typeId: string,
  fieldSlug: string,
  companyId: string | null,
): Promise<DocumentTypeFieldsResponse> {
  const res = await api.delete<DocumentTypeFieldsResponse>(
    `/document-types/${typeId}/fields/${encodeURIComponent(fieldSlug)}${companyQuery(companyId)}`,
  );
  return res.data;
}

/** „Przywróć ustawienie ITE” dla całego typu w firmie. */
export async function resetDocumentTypeFields(
  typeId: string,
  companyId: string | null,
): Promise<DocumentTypeFieldsResponse> {
  const res = await api.delete<DocumentTypeFieldsResponse>(
    `/document-types/${typeId}/fields${companyQuery(companyId)}`,
  );
  return res.data;
}

export async function fetchDocumentTypeFieldsSummary(
  docClass: DocumentClass,
  companyId: string | null,
): Promise<DocumentTypeFieldsSummaryItem[]> {
  const params = new URLSearchParams({ class: docClass });
  if (companyId) params.set('companyId', companyId);
  const res = await api.get<DocumentTypeFieldsSummaryResponse>(
    `/document-types/fields-summary?${params.toString()}`,
  );
  return res.data.summaries;
}

// ── Logika czysta ───────────────────────────────────────────────────────────────

/** Klucz pary (typ, firma) w mapie podsumowań. */
export function summaryPairKey(typeId: string, companyId: string | null): string {
  return `${typeId}:${companyId ?? 'null'}`;
}

/** Mapa slug → stan pola w typie (karta dokumentu, silnik formatek). */
export function toTypeFieldStates(
  fields: ReadonlyArray<Pick<EffectiveDocumentTypeField, 'slug' | 'state'>>,
): ReadonlyMap<string, DocumentTypeFieldState> {
  return new Map(fields.map((f) => [f.slug, f.state]));
}

/** Czy stan pola oznacza odstępstwo od ustawienia ITE (do kropki i filtra „Tylko zmienione”). */
export function isDeviation(
  field: Pick<EffectiveDocumentTypeField, 'overridden' | 'state' | 'baseState'>,
): boolean {
  return field.overridden && field.state !== field.baseState;
}

/**
 * Sprawdzenie obecności wymaganych pól dodatkowych przed wysyłką dokumentu. Zwraca slugi pól
 * wymaganych, które są puste.
 *
 * Wymagalność z typu (`typeFieldStates`): `excluded` → pole pomijane, `required` → sprawdzane,
 * `optional` → pomijane. Pole spoza mapy albo brak mapy (dokument bez typu, dane jeszcze
 * niewczytane) → flaga wymagalności pola klasy, gdy `classFlagFallback` (klasy, które już dziś
 * sprawdzają ją przed wysyłką); w przeciwnym razie pole nie jest sprawdzane.
 */
export function collectMissingExtRequired(params: {
  fields: ReadonlyArray<EngineFormField>;
  extValues: Readonly<Record<string, unknown>>;
  typeFieldStates: ReadonlyMap<string, DocumentTypeFieldState> | null;
  classFlagFallback: boolean;
}): Set<string> {
  const { fields, extValues, typeFieldStates, classFlagFallback } = params;
  const missing = new Set<string>();
  for (const field of fields) {
    if (field.isCore || field.gcrecord !== null) continue;
    const state = typeFieldStates?.get(field.slug);
    const required =
      state !== undefined ? state === 'required' : classFlagFallback && field.isRequired;
    if (!required) continue;
    const value = extValues[field.slug];
    const isEmpty = value == null || (typeof value === 'string' && value.trim() === '');
    if (isEmpty) missing.add(field.slug);
  }
  return missing;
}
